import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  CloseButton,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  TableContainer,
  Select,
  Badge,
  useToast,
  Text,
  Flex,
  Heading,
  Card,
  CardBody,
  SimpleGrid,
  useColorModeValue,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Tooltip,
  Portal,
  InputGroup,
  InputLeftElement,
  Stack,
} from '@chakra-ui/react';
import { supabase } from '../lib/supabase';
import { formatSupabaseError } from '../utils/error';
import { AddIcon, ChevronDownIcon, ViewIcon, EditIcon, DeleteIcon, PhoneIcon, SearchIcon, DownloadIcon, CloseIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import CHITOOR_LOCATIONS from '../data/chitoorLocations';

interface ChitoorProject {
  id: string;
  customer_name: string;
  mobile_number: string;
  date_of_order: string;
  service_number?: string;
  address_mandal_village: string;
  capacity: number;
  project_cost: number;
  amount_received?: number;
  subsidy_scope?: string;
  velugu_officer_payments?: number;
  project_status?: string;
  material_sent_date?: string;
  balamuragan_payment?: number;
  lead_source?: string;
  lead_finished_by?: string;
  lead_finished_by_name?: string;
}

interface StatsCardProps {
  title: string;
  value: string | number;
  icon: string;
  color: string;
  helpText?: string;
}

const StatsCard: React.FC<StatsCardProps> = ({ title, value, icon, color, helpText }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const iconBg = useColorModeValue(`${color}.50`, `${color}.900`);

  return (
    <Card bg={cardBg} shadow="sm" border="1px solid" borderColor="gray.100">
      <CardBody>
        <Box display="flex" alignItems="center" gap={4}>
          <Box
            w="12"
            h="12"
            bg={iconBg}
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="xl"
            color={`${color}.600`}
          >
            {icon}
          </Box>
          <Box>
            <Text color="gray.600" fontSize="sm" fontWeight="medium">
              {title}
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color={`${color}.600`}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Text>
            {helpText && (
              <Text color="gray.500" fontSize="xs">
                {helpText}
              </Text>
            )}
          </Box>
        </Box>
      </CardBody>
    </Card>
  );
};


const ChitoorProjects = () => {
  const [projects, setProjects] = useState<ChitoorProject[]>([]);
  const [allProjects, setAllProjects] = useState<ChitoorProject[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const [newProject, setNewProject] = useState({
    customer_name: '',
    mobile_number: '',
    date_of_order: '',
    service_number: '',
    address_mandal_village: '',
    capacity: '',
    project_cost: '',
    amount_received: '',
    subsidy_scope: '',
    velugu_officer_payments: '',
    project_status: 'Pending',
    material_sent_date: '',
    balamuragan_payment: '',
    lead_source: '',
    lead_finished_by: '',
    lead_finished_by_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalProjects: 0,
    pendingProjects: 0,
    completedProjects: 0,
    cancelledProjects: 0,
    totalRevenue: 0,
    totalCapacity: 0,
  });
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const fetchChitoorProjects = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('chitoor_projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', (error as any)?.message || error, error);
        toast({
          title: 'Error',
          description: `Failed to fetch Chitoor projects. ${formatSupabaseError(error)}`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (data) {
        setAllProjects(data);
        const completedProjects = data.filter((p: any) => (p.project_status || '').toLowerCase() === 'completed');
        const cancelledProjects = data.filter((p: any) => (p.project_status || '').toLowerCase() === 'cancelled');
        const pendingProjects = data.filter((p: any) =>
          (p.project_status || '').toLowerCase() !== 'completed' &&
          (p.project_status || '').toLowerCase() !== 'cancelled'
        );
        const totalRevenue = data.reduce((sum: number, p: any) => sum + (p.project_cost || 0), 0);
        const totalCapacity = data.reduce((sum: number, p: any) => sum + (p.capacity || 0), 0);

        setStats({
          totalProjects: data.length,
          pendingProjects: pendingProjects.length,
          completedProjects: completedProjects.length,
          cancelledProjects: cancelledProjects.length,
          totalRevenue,
          totalCapacity,
        });
      }
    } catch (error) {
      console.error('Error fetching Chitoor projects:', error);
      toast({ title: 'Error', description: 'An unexpected error occurred', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Apply search filter whenever searchTerm or allProjects changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setProjects(allProjects);
    } else {
      const keyword = searchTerm.toLowerCase();
      const filtered = allProjects.filter((project: ChitoorProject) =>
        Object.values(project).some(val =>
          val !== null && val !== undefined && String(val).toLowerCase().includes(keyword)
        )
      );
      setProjects(filtered);
    }
  }, [searchTerm, allProjects]);

  // Locations state
  const [locations, setLocations] = useState<{ village: string; mandal: string }[]>([]);
  const [selectedMandal, setSelectedMandal] = useState('');
  const [selectedVillage, setSelectedVillage] = useState('');
  // Filter text inputs (for 2-letter filtering behavior)
  const [mandalFilter, setMandalFilter] = useState('');
  const [villageFilter, setVillageFilter] = useState('');

  const villagesByMandal = useMemo(() => {
    const map = new Map<string, Set<string>>();
    locations.forEach(({ mandal, village }) => {
      const key = mandal.trim();
      if (!map.has(key)) map.set(key, new Set());
      map.get(key)!.add(village.trim());
    });
    return map;
  }, [locations]);

  const mandalByVillage = useMemo(() => {
    const map = new Map<string, string>();
    locations.forEach(({ mandal, village }) => {
      map.set(village.trim(), mandal.trim());
    });
    return map;
  }, [locations]);

  const uniqueMandals = useMemo(() => Array.from(new Set(locations.map((l) => l.mandal.trim()))).sort(), [locations]);
  const uniqueVillages = useMemo(() => Array.from(new Set(locations.map((l) => l.village.trim()))).sort(), [locations]);

  // Filtered lists with 2-letter minimum filter. If filter is less than 2 letters, show full lists.
  const filteredMandals = useMemo(() => {
    const q = mandalFilter.trim().toLowerCase();
    if (q.length >= 2) return uniqueMandals.filter((m) => m.toLowerCase().includes(q));
    return uniqueMandals;
  }, [mandalFilter, uniqueMandals]);

  const filteredVillages = useMemo(() => {
    const q = villageFilter.trim().toLowerCase();
    const base = selectedMandal ? Array.from(villagesByMandal.get(selectedMandal) || []) : uniqueVillages;
    if (q.length >= 2) return base.filter((v) => v.toLowerCase().includes(q));
    return base;
  }, [villageFilter, selectedMandal, villagesByMandal, uniqueVillages]);

  // Use embedded locations as primary source (fallback to fetch if needed)
  useEffect(() => {
    if (CHITOOR_LOCATIONS && CHITOOR_LOCATIONS.length > 0) {
      setLocations(CHITOOR_LOCATIONS);
      return;
    }

    const load = async () => {
      try {
        const path = (process.env.PUBLIC_URL || '') + '/data/chitoor_locations.csv';
        const res = await fetch(path, { cache: 'no-store' });
        if (!res.ok) throw new Error('CSV fetch failed');
        const text = await res.text();
        if (/^\s*<!doctype html>|<meta|<script|<html/i.test(text)) throw new Error('CSV fetch returned HTML');
        const lines = text.split(/\r?\n/).filter(Boolean);
        const out: { village: string; mandal: string }[] = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i];
          const parts = line.split(',');
          if (parts.length < 2) continue;
          const village = parts[0];
          const mandal = parts.slice(1).join(',');
          if (!village || !mandal) continue;
          out.push({ village: village.trim(), mandal: mandal.trim() });
        }
        if (out.length > 0) setLocations(out);
      } catch (e) {
        console.error('Failed to load locations CSV from path', e);
      }
    };

    if (isOpen && locations.length === 0) load();
  }, [isOpen, locations.length]);

  useEffect(() => {
    fetchChitoorProjects();
    const ch = (supabase as any)
      .channel('realtime-chitoor-projects-page')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chitoor_projects' }, () => fetchChitoorProjects())
      .subscribe();
    return () => {
      try { (ch as any)?.unsubscribe?.(); } catch {}
    };
  }, [fetchChitoorProjects]);

  useEffect(() => {
    const parts: string[] = [];
    if (selectedMandal) parts.push(selectedMandal);
    if (selectedVillage) parts.push(selectedVillage);
    const combined = parts.join(', ');
    if (combined) {
      setNewProject((prev) => ({ ...prev, address_mandal_village: combined }));
    }
  }, [selectedMandal, selectedVillage]);

  const exportToExcel = () => {
    if (projects.length === 0) {
      toast({
        title: 'Export Error',
        description: 'No projects available to export',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const dataToExport = projects.map(project => ({
      'Project ID': `Chitoor-${project.id.slice(-6)}`,
      'Customer Name': project.customer_name,
      'Mobile Number': project.mobile_number,
      'Date of Order': project.date_of_order ? new Date(project.date_of_order).toLocaleDateString() : 'N/A',
      'Service Number': project.service_number || 'N/A',
      'Address (Mandal, Village)': project.address_mandal_village,
      'Capacity (kW)': project.capacity,
      'Project Cost': project.project_cost,
      'Amount Received': project.amount_received || 0,
      'Balance': (project.project_cost || 0) - (project.amount_received || 0),
      'Subsidy Scope': project.subsidy_scope || 'N/A',
      'Velugu Officer Payments': project.velugu_officer_payments || 0,
      'Project Status': project.project_status || 'Pending',
      'Material Sent Date': project.material_sent_date ? new Date(project.material_sent_date).toLocaleDateString() : 'N/A',
      'Balamuragan Payment': project.balamuragan_payment || 0,
      'Lead Source': project.lead_source || 'N/A',
      'Lead Finished By': project.lead_finished_by_name || 'N/A',
      'Lead Finished Date': project.lead_finished_by ? new Date(project.lead_finished_by).toLocaleDateString() : 'N/A',
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Chitoor Projects');

    const fileName = `chitoor_projects_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    toast({
      title: 'Success',
      description: `Exported ${projects.length} Chitoor projects to Excel`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'capacity') {
      const mappedCost = value === '2' ? '148000' : (value === '3' ? '205000' : newProject.project_cost);
      setNewProject(prev => ({ ...prev, capacity: value, project_cost: mappedCost }));
      return;
    }
    setNewProject(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const projectData = {
        customer_name: newProject.customer_name,
        mobile_number: newProject.mobile_number,
        date_of_order: newProject.date_of_order,
        service_number: newProject.service_number || null,
        address_mandal_village: newProject.address_mandal_village,
        capacity: parseFloat(newProject.capacity),
        project_cost: parseFloat(newProject.project_cost),
        amount_received: newProject.amount_received ? parseFloat(newProject.amount_received) : null,
        subsidy_scope: newProject.subsidy_scope || null,
        velugu_officer_payments: newProject.velugu_officer_payments ? parseFloat(newProject.velugu_officer_payments) : null,
        project_status: newProject.project_status || 'Pending',
        material_sent_date: newProject.material_sent_date || null,
        balamuragan_payment: newProject.balamuragan_payment ? parseFloat(newProject.balamuragan_payment) : null,
        lead_source: newProject.lead_source || null,
        lead_finished_by: newProject.lead_finished_by || null,
        lead_finished_by_name: newProject.lead_finished_by_name || null,
      };

      const { error } = await supabase
        .from('chitoor_projects')
        .insert([projectData]);

      if (error) {
        console.error('Supabase error:', (error as any)?.message || error, error);
        toast({
          title: 'Error',
          description: `Failed to create project. ${formatSupabaseError(error)}`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      toast({
        title: 'Success',
        description: 'Chitoor project created successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
      setNewProject({
        customer_name: '',
        mobile_number: '',
        date_of_order: '',
        service_number: '',
        address_mandal_village: '',
        capacity: '',
        project_cost: '',
        amount_received: '',
        subsidy_scope: '',
        velugu_officer_payments: '',
        project_status: 'Pending',
        material_sent_date: '',
        balamuragan_payment: '',
        lead_source: '',
        lead_finished_by: '',
        lead_finished_by_name: '',
      });
      setSelectedMandal('');
      setSelectedVillage('');
      fetchChitoorProjects();
    } catch (error) {
      console.error('Error creating Chitoor project:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'green';
      case 'installation completed': return 'green';
      case 'pending': return 'yellow';
      case 'material pending': return 'yellow';
      case 'in progress': return 'blue';
      case 'material sent': return 'purple';
      case 'on hold': return 'red';
      case 'cancelled': return 'orange';
      default: return 'gray';
    }
  };

  return (
    <Box>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" color="gray.800" mb={2}>
              Chitoor Projects Dashboard
            </Heading>
            <Text color="gray.600">
              Overview and management of Chitoor district projects (AP region with dedicated tracking)
            </Text>
          </Box>
          <Button variant="ghost" onClick={() => navigate(-1)}>Back</Button>
        </Flex>

        {/* Stats Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 6 }} spacing={6}>
          <StatsCard
            title="Total Projects"
            value={stats.totalProjects}
            icon="📋"
            color="blue"
            helpText="All Chitoor projects"
          />
          <StatsCard
            title="Active Projects"
            value={stats.pendingProjects}
            icon="🚀"
            color="green"
            helpText="In progress"
          />
          <StatsCard
            title="Completed Projects"
            value={stats.completedProjects}
            icon="✅"
            color="green"
            helpText="Successfully delivered"
          />
          <StatsCard
            title="Cancelled Projects"
            value={stats.cancelledProjects}
            icon="❌"
            color="orange"
            helpText="Withdrawn Leads"
          />
          <StatsCard
            title="Total Revenue"
            value={`₹${stats.totalRevenue.toLocaleString()}`}
            icon="💰"
            color="orange"
            helpText="Project value"
          />
          <StatsCard
            title="Total Capacity"
            value={`${stats.totalCapacity.toLocaleString()} kW`}
            icon="⚡"
            color="purple"
            helpText="Energy capacity"
          />
        </SimpleGrid>

        {/* Search Bar */}
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody>
            <Stack spacing={4}>
              <InputGroup>
                <InputLeftElement>
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search Chitoor projects by any keyword..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  _focus={{ bg: 'white', borderColor: 'green.400' }}
                />
              </InputGroup>
            </Stack>
          </CardBody>
        </Card>

        {/* Header Actions */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <Box>
            <Heading size="md" color="gray.800" mb={2}>
              Chitoor Projects Management
            </Heading>
            <Text color="gray.600">
              {projects.length} of {allProjects.length} projects
              {searchTerm && ` matching "${searchTerm}"`}
              </Text>
            </Box>
            <HStack spacing={4}>
              <Button
                leftIcon={<DownloadIcon />}
                onClick={exportToExcel}
                colorScheme="green"
                variant="outline"
                size="lg"
                borderRadius="lg"
                _hover={{ bg: 'green.50', transform: 'translateY(-1px)' }}
                transition="all 0.2s"
                isDisabled={projects.length === 0}
              >
                Export to Excel
              </Button>
              <Button
                leftIcon={<AddIcon />}
                colorScheme="green"
                onClick={onOpen}
                size="lg"
                borderRadius="lg"
                _hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}
                transition="all 0.2s"
              >
                Create New Chitoor Project
              </Button>
            </HStack>
          </Flex>


        {/* Projects Table */}
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody p={0}>
            {projects.length > 0 ? (
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th fontWeight="semibold" color="gray.700">Project Details</Th>
                      <Th fontWeight="semibold" color="gray.700">Customer Info</Th>
                      <Th fontWeight="semibold" color="gray.700">Financial</Th>
                      <Th fontWeight="semibold" color="gray.700">Timeline</Th>
                      <Th fontWeight="semibold" color="gray.700">Lead Info</Th>
                      <Th fontWeight="semibold" color="gray.700">Status</Th>
                      <Th fontWeight="semibold" color="gray.700">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {projects.map(project => (
                      <Tr key={project.id} _hover={{ bg: 'gray.50' }} transition="all 0.2s">
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium" fontSize="sm" cursor="pointer"
                                  onClick={() => navigate(`/projects/chitoor/${project.id}`)}>
                              Chitoor-{project.id.slice(-6)}
                            </Text>
                            <HStack spacing={2}>
                              <Badge colorScheme="purple" size="sm">
                                {project.capacity} kW
                              </Badge>
                              <Badge variant="outline" size="sm">
                                {project.subsidy_scope || 'N/A'}
                              </Badge>
                            </HStack>
                            <HStack spacing={1}>
                              <Text fontSize="xs" color="yellow.500">⚡</Text>
                              <Text fontSize="xs" color="gray.600">
                                {project.capacity || 'N/A'} kW
                              </Text>
                            </HStack>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <HStack spacing={1}>
                              <Text fontSize="xs" color="gray.400">👤</Text>
                              <Text fontSize="sm" fontWeight="medium">
                                {project.customer_name}
                              </Text>
                            </HStack>
                            <HStack spacing={1}>
                              <PhoneIcon color="gray.400" boxSize={3} />
                              <Text fontSize="xs" color="gray.600">
                                {project.mobile_number}
                              </Text>
                            </HStack>
                            <HStack spacing={1}>
                              <Text fontSize="xs" color="gray.400">📍</Text>
                              <Text fontSize="xs" color="gray.600" isTruncated maxW="150px">
                                {project.address_mandal_village}
                              </Text>
                            </HStack>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <HStack spacing={1}>
                              <Text fontSize="xs" color="green.500">₹</Text>
                              <Text fontSize="sm" fontWeight="bold" color="green.600">
                                ₹{project.project_cost.toLocaleString()}
                              </Text>
                            </HStack>
                            <Text fontSize="xs" color="blue.600">
                              Received: ₹{project.amount_received?.toLocaleString() || '0'}
                            </Text>
                            <Text fontSize="xs" color="orange.600">
                              Balance: ₹{((project.project_cost || 0) - (project.amount_received || 0)).toLocaleString()}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Tooltip label="Order Date">
                              <HStack spacing={1}>
                                <Text fontSize="xs" color="gray.400">📅</Text>
                                <Text fontSize="xs" color="gray.600">
                                  {project.date_of_order ? new Date(project.date_of_order).toLocaleDateString() : 'N/A'}
                                </Text>
                              </HStack>
                            </Tooltip>
                            {project.material_sent_date && (
                              <Tooltip label="Material Sent Date">
                                <HStack spacing={1}>
                                  <Text fontSize="xs" color="purple.400">📦</Text>
                                  <Text fontSize="xs" color="purple.600">
                                    {new Date(project.material_sent_date).toLocaleDateString()}
                                  </Text>
                                </HStack>
                              </Tooltip>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            {project.lead_source && (
                              <HStack spacing={1}>
                                <Text fontSize="xs" color="blue.500">🎯</Text>
                                <Text fontSize="xs" color="blue.600" fontWeight="medium">
                                  {project.lead_source}
                                </Text>
                              </HStack>
                            )}
                            {(project.lead_finished_by || project.lead_finished_by_name) && (
                              <Tooltip label="Lead Finished By">
                                <VStack align="start" spacing={0}>
                                  {project.lead_finished_by_name && (
                                    <Text fontSize="xs" color="purple.600" fontWeight="medium">
                                      👤 {project.lead_finished_by_name}
                                    </Text>
                                  )}
                                  {project.lead_finished_by && (
                                    <HStack spacing={1}>
                                      <Text fontSize="xs" color="orange.400">⏱️</Text>
                                      <Text fontSize="xs" color="orange.600">
                                        {new Date(project.lead_finished_by).toLocaleDateString()}
                                      </Text>
                                    </HStack>
                                  )}
                                </VStack>
                              </Tooltip>
                            )}
                            {!project.lead_source && !project.lead_finished_by && !project.lead_finished_by_name && (
                              <Text fontSize="xs" color="gray.400">—</Text>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={getStatusColor(project.project_status || 'pending')}
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="xs"
                          >
                            {project.project_status || 'Pending'}
                          </Badge>
                        </Td>
                        <Td>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<ChevronDownIcon />}
                              variant="ghost"
                              size="sm"
                            />
                            <Portal>
                              <Portal>
                              <MenuList>
                                <MenuItem
                                  icon={<EditIcon />}
                                  onClick={() => navigate(`/projects/chitoor/${project.id}?edit=customer`)}
                                >
                                  Edit Customer
                                </MenuItem>
                                <MenuItem
                                  icon={<EditIcon />}
                                  onClick={() => navigate(`/projects/chitoor/${project.id}?edit=project`)}
                                >
                                  Edit Project
                                </MenuItem>
                                <MenuItem
                                  icon={<ViewIcon />}
                                  onClick={() => navigate(`/projects/chitoor/${project.id}`)}
                                >
                                  View Details
                                </MenuItem>
                                <MenuItem
                                  icon={<Text>✅</Text>}
                                  onClick={async () => {
                                    try {
                                      const { error } = await supabase
                                        .from('chitoor_projects')
                                        .update({ project_status: 'Completed' })
                                        .eq('id', project.id);
                                      if (error) throw error;
                                      toast({ title: 'Marked complete', status: 'success', duration: 2000, isClosable: true });
                                      fetchChitoorProjects();
                                    } catch (e) {
                                      toast({ title: 'Failed to mark complete', description: formatSupabaseError(e), status: 'error', duration: 3000, isClosable: true });
                                    }
                                  }}
                                >
                                  Mark Complete
                                </MenuItem>
                                <MenuItem
                                  icon={<CloseIcon />}
                                  onClick={async () => {
                                    if (!window.confirm('Are you sure you want to cancel this project?')) return;
                                    try {
                                      const { error } = await supabase
                                        .from('chitoor_projects')
                                        .update({ project_status: 'Cancelled' })
                                        .eq('id', project.id);
                                      if (error) throw error;
                                      toast({ title: 'Project cancelled', status: 'success', duration: 2000, isClosable: true });
                                      fetchChitoorProjects();
                                    } catch (e) {
                                      toast({ title: 'Failed to cancel', description: formatSupabaseError(e), status: 'error', duration: 3000, isClosable: true });
                                    }
                                  }}
                                >
                                  Mark Cancelled
                                </MenuItem>
                                <MenuItem
                                  icon={<DeleteIcon />}
                                  color="red.500"
                                  onClick={async () => {
                                    if (!window.confirm('Delete this project permanently?')) return;
                                    try {
                                      const { error } = await supabase
                                        .from('chitoor_projects')
                                        .delete()
                                        .eq('id', project.id);
                                      if (error) throw error;
                                      toast({ title: 'Project deleted', status: 'success', duration: 2000, isClosable: true });
                                      fetchChitoorProjects();
                                    } catch (e) {
                                      toast({ title: 'Failed to delete', description: formatSupabaseError(e), status: 'error', duration: 3000, isClosable: true });
                                    }
                                  }}
                                >
                                  Delete Project
                                </MenuItem>
                              </MenuList>
                            </Portal>
                            </Portal>
                          </Menu>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : (
              <Flex direction="column" align="center" py={16}>
                <Text fontSize="6xl" color="gray.300" mb={4}>📊</Text>
                <Text color="gray.500" fontSize="lg" fontWeight="medium" mb={2}>
                  No Chitoor projects found
                </Text>
                <Text color="gray.400" fontSize="sm" mb={6}>
                  Create your first Chitoor project to get started
                </Text>
                <Button
                  leftIcon={<AddIcon />}
                  colorScheme="green"
                  onClick={onOpen}
                >
                  Create New Chitoor Project
                </Button>
              </Flex>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Create Chitoor Project Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex align="center" gap={2}>
              <AddIcon color="green.500" />
              Create New Chitoor Project
            </Flex>
          </ModalHeader>
          <CloseButton position="absolute" right={2} top={2} onClick={onClose} />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} w="full">
                

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Customer Name <Text as="span" color="red.500">*</Text></FormLabel>
                  <Input
                    name="customer_name"
                    value={newProject.customer_name}
                    onChange={handleInputChange}
                    placeholder="Enter customer name"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Mobile Number <Text as="span" color="red.500">*</Text></FormLabel>
                  <Input
                    name="mobile_number"
                    value={newProject.mobile_number}
                    onChange={handleInputChange}
                    placeholder="+91 98765 43210"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Date of Order <Text as="span" color="red.500">*</Text></FormLabel>
                  <Input
                    name="date_of_order"
                    type="date"
                    value={newProject.date_of_order}
                    onChange={handleInputChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Service Number</FormLabel>
                  <Input
                    name="service_number"
                    value={newProject.service_number}
                    onChange={handleInputChange}
                    placeholder="Service number"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Mandal</FormLabel>
                  <Input
                    placeholder="Type 2+ letters to filter"
                    value={mandalFilter}
                    onChange={(e) => setMandalFilter(e.target.value)}
                    mb={2}
                  />
                  <Select
                    placeholder="Select mandal"
                    value={selectedMandal}
                    onChange={(e) => {
                      const val = e.target.value;
                                      setSelectedMandal(val);
                      // reset village if it doesn't belong
                      if (selectedVillage && (villagesByMandal.get(val)?.has(selectedVillage) !== true)) {
                        setSelectedVillage('');
                      }
                    }}
                  >
                    {filteredMandals.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Village</FormLabel>
                  <Input
                    placeholder="Type 2+ letters to filter"
                    value={villageFilter}
                    onChange={(e) => setVillageFilter(e.target.value)}
                    mb={2}
                  />
                  <Select
                    placeholder="Select village"
                    value={selectedVillage}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedVillage(val);
                      const mandal = mandalByVillage.get(val) || '';
                      if (mandal) {
                        setSelectedMandal(mandal);
                      }
                    }}
                  >
                    {filteredVillages.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Address (Mandal, Village) <Text as="span" color="red.500">*</Text></FormLabel>
                  <Input
                    name="address_mandal_village"
                    value={newProject.address_mandal_village}
                    onChange={handleInputChange}
                    placeholder="Mandal, Village"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Capacity (kW) <Text as="span" color="red.500">*</Text></FormLabel>
                  <Select
                    name="capacity"
                    value={newProject.capacity}
                    onChange={handleInputChange}
                    placeholder="Select capacity"
                  >
                    <option value="2">2 kW</option>
                    <option value="3">3 kW</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium">Project Cost (���) <Text as="span" color="red.500">*</Text></FormLabel>
                  <Select
                    name="project_cost"
                    value={newProject.project_cost}
                    onChange={handleInputChange}
                    placeholder="Select total amount"
                  >
                    <option value="148000">₹1,48,000 (2 kW)</option>
                    <option value="205000">₹2,05,000 (3 kW)</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Amount Received (₹)</FormLabel>
                  <Input
                    name="amount_received"
                    type="number"
                    value={newProject.amount_received}
                    onChange={handleInputChange}
                    placeholder="0"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Subsidy Scope</FormLabel>
                  <Select
                    name="subsidy_scope"
                    value={newProject.subsidy_scope}
                    onChange={handleInputChange}
                    placeholder="Select subsidy scope"
                  >
                    <option value="Axiso">Axiso</option>
                    <option value="Customer (in Chitoor)">Customer (in Chitoor)</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Velugu Officer Payments (₹)</FormLabel>
                  <Input
                    name="velugu_officer_payments"
                    type="number"
                    value={newProject.velugu_officer_payments}
                    onChange={handleInputChange}
                    placeholder="0"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Project Status</FormLabel>
                  <Select
                    name="project_status"
                    value={newProject.project_status}
                    onChange={handleInputChange}
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="Installation Completed">Installation Completed</option>
                    <option value="Material Sent">Material Sent</option>
                    <option value="Material Pending">Material Pending</option>
                    <option value="On Hold">On Hold</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Material Sent Date</FormLabel>
                  <Input
                    name="material_sent_date"
                    type="date"
                    value={newProject.material_sent_date}
                    onChange={handleInputChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Balamuragan Payment (₹)</FormLabel>
                  <Input
                    name="balamuragan_payment"
                    type="number"
                    value={newProject.balamuragan_payment}
                    onChange={handleInputChange}
                    placeholder="0"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Lead Source</FormLabel>
                  <Select
                    name="lead_source"
                    value={newProject.lead_source}
                    onChange={handleInputChange}
                    placeholder="Select lead source"
                  >
                    <option value="Online">Online</option>
                    <option value="Link">Link</option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="Referral">Referral</option>
                    <option value="Advertisement">Advertisement</option>
                    <option value="Direct">Direct</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Event">Event</option>
                    <option value="Other">Other</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Lead Finished By Name</FormLabel>
                  <Input
                    name="lead_finished_by_name"
                    type="text"
                    value={newProject.lead_finished_by_name}
                    onChange={handleInputChange}
                    placeholder="Enter person's name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">Lead Finished By Date</FormLabel>
                  <Input
                    name="lead_finished_by"
                    type="date"
                    value={newProject.lead_finished_by}
                    onChange={handleInputChange}
                  />
                </FormControl>
              </SimpleGrid>

              <Divider />

              <Button 
                colorScheme="green" 
                width="full" 
                onClick={handleSubmit}
                isLoading={loading}
                loadingText="Creating..."
                leftIcon={<AddIcon />}
                size="lg"
              >
                Create Chitoor Project
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ChitoorProjects;
